import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BusinessRulesManager from './BusinessRulesManager';
import RuleUpgradeManager from './RuleUpgradeManager';
import AuditTrailViewer from './AuditTrailViewer';
import { NotificationCenter } from './NotificationCenter';
import LocalStorageDebug from '../debug/LocalStorageDebug';
import { InlineDemoNotice } from '../common/DemoModeIndicator';

// Sample user data for the admin dashboard
const sampleUsers = [
  { id: '1', name: 'Admin User', email: 'admin@roiexteriors.com', role: 'admin', lastLogin: '2 hours ago' },
  { id: '2', name: 'Regular User', email: 'user@roiexteriors.com', role: 'user', lastLogin: '1 day ago' },
  { id: '3', name: 'John Smith', email: 'john@example.com', role: 'user', lastLogin: '3 days ago' },
  { id: '4', name: 'Jane Doe', email: 'jane@example.com', role: 'user', lastLogin: '1 week ago' },
];

// Sample report data
const sampleReports = [
  { id: '1', name: 'March 2025 Report', createdBy: 'Admin User', createdAt: '2025-04-01', views: 23 },
  { id: '2', name: 'February 2025 Report', createdBy: 'Admin User', createdAt: '2025-03-01', views: 45 },
  { id: '3', name: 'January 2025 Report', createdBy: 'John Smith', createdAt: '2025-02-01', views: 62 },
  { id: '4', name: 'Q4 2024 Summary', createdBy: 'Admin User', createdAt: '2025-01-05', views: 87 },
];

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  if (user?.role !== 'admin') {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="bg-red-100 rounded-lg p-4 text-red-700">
          <p>You do not have permission to access the admin dashboard.</p>
          <div className="mt-4 p-4 bg-gray-100 rounded text-sm text-gray-700">
            <strong>Debug Info:</strong>
            <div>User: {user ? JSON.stringify(user, null, 2) : 'null'}</div>
            <div>User Role: {user?.role || 'undefined'}</div>
            <div>Is Admin: {user?.role === 'admin' ? 'Yes' : 'No'}</div>
          </div>
          <div className="mt-4">
            <LocalStorageDebug />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h1 className="text-2xl font-bold mb-4">Admin Dashboard</h1>
        <p className="text-gray-600">
          Manage users, reports, and system settings for the ROI Exteriors Marketing Analytics Portal.
        </p>
      </div>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button
              className={`px-4 py-4 text-sm font-medium ${
                activeTab === 'overview'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`px-4 py-4 text-sm font-medium ${
                activeTab === 'users'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('users')}
            >
              Users
            </button>
            <button
              className={`px-4 py-4 text-sm font-medium ${
                activeTab === 'reports'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('reports')}
            >
              Reports
            </button>
            <button
              className={`px-4 py-4 text-sm font-medium ${
                activeTab === 'settings'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </button>
            <button
              className={`px-4 py-4 text-sm font-medium ${
                activeTab === 'business-rules'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('business-rules')}
            >
              Business Rules
            </button>
            <button
              className={`px-4 py-4 text-sm font-medium ${
                activeTab === 'bulk-operations'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('bulk-operations')}
            >
              Bulk Operations
            </button>
            <button
              className={`px-4 py-4 text-sm font-medium ${
                activeTab === 'audit-trail'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('audit-trail')}
            >
              Audit Trail
            </button>
            <button
              className={`px-4 py-4 text-sm font-medium ${
                activeTab === 'notifications'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab('notifications')}
            >
              Notifications
            </button>
          </nav>
        </div>
        
        <div className="p-4">
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-lg font-semibold mb-6">CSV-to-Report Automation</h2>
              
              <InlineDemoNotice>
                <p>
                  The overview tab shows sample user and report statistics. 
                  All other admin features (Business Rules, Bulk Operations, Audit Trail, Notifications) 
                  are fully functional when connected to the Laravel backend.
                </p>
              </InlineDemoNotice>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      📂
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Generate Report</h3>
                      <p className="text-sm text-gray-500">Upload CSV files to create automated reports</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/admin/csv-generator')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
                  >
                    Upload CSV File
                  </button>
                </div>
                
                <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      📊
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Manage Reports</h3>
                      <p className="text-sm text-gray-500">View, edit, and delete generated reports</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/admin/reports')}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium"
                  >
                    View All Reports
                  </button>
                </div>
                
                <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                      ⚙️
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Business Rules</h3>
                      <p className="text-sm text-gray-500">Configure commission tiers and calculations</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/admin/business-rules')}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium"
                  >
                    Manage Rules
                  </button>
                </div>
                
                <div className="border rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                      ⬆️
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Rule Upgrades</h3>
                      <p className="text-sm text-gray-500">Bulk upgrade reports to latest rules</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/admin/rule-upgrades')}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-medium"
                  >
                    Upgrade Reports
                  </button>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 text-blue-600 mr-3 mt-0.5">ℹ️</div>
                  <div>
                    <h4 className="font-medium text-blue-900 mb-1">How it works</h4>
                    <p className="text-sm text-blue-700 mb-2">
                      The CSV-to-Report automation system allows you to upload CSV files and automatically 
                      generate professional marketing reports with the same format as your static reports.
                    </p>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Upload sales data or ad spend CSV files</li>
                      <li>• Choose between standard or Cody Cline calculations</li>
                      <li>• View generated reports with full functionality</li>
                      <li>• Export reports to PDF for sharing</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'users' && (
            <div>
              <div className="flex justify-between mb-4">
                <h2 className="text-lg font-semibold">User Management</h2>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
                  Add User
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-4 text-left">Name</th>
                      <th className="py-2 px-4 text-left">Email</th>
                      <th className="py-2 px-4 text-left">Role</th>
                      <th className="py-2 px-4 text-left">Last Login</th>
                      <th className="py-2 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleUsers.map((user, index) => (
                      <tr key={user.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="py-2 px-4">{user.name}</td>
                        <td className="py-2 px-4">{user.email}</td>
                        <td className="py-2 px-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="py-2 px-4">{user.lastLogin}</td>
                        <td className="py-2 px-4 text-right">
                          <button className="text-blue-600 hover:text-blue-800 mr-2">Edit</button>
                          <button className="text-red-600 hover:text-red-800">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 text-sm text-gray-500">
                <p>Note: This is a demo. User management actions are not functional.</p>
              </div>
            </div>
          )}
          
          {activeTab === 'reports' && (
            <div>
              <div className="flex justify-between mb-4">
                <h2 className="text-lg font-semibold">Report Management</h2>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
                  Create Report
                </button>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-4 text-left">Report Name</th>
                      <th className="py-2 px-4 text-left">Created By</th>
                      <th className="py-2 px-4 text-left">Created Date</th>
                      <th className="py-2 px-4 text-center">Views</th>
                      <th className="py-2 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleReports.map((report, index) => (
                      <tr key={report.id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="py-2 px-4">{report.name}</td>
                        <td className="py-2 px-4">{report.createdBy}</td>
                        <td className="py-2 px-4">{report.createdAt}</td>
                        <td className="py-2 px-4 text-center">{report.views}</td>
                        <td className="py-2 px-4 text-right">
                          <button className="text-green-600 hover:text-green-800 mr-2">View</button>
                          <button className="text-blue-600 hover:text-blue-800 mr-2">Edit</button>
                          <button className="text-red-600 hover:text-red-800">Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 text-sm text-gray-500">
                <p>Note: This is a demo. Report management actions are not functional.</p>
              </div>
            </div>
          )}
          
          {activeTab === 'settings' && (
            <div>
              <h2 className="text-lg font-semibold mb-4">System Settings</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border rounded p-4">
                  <h3 className="font-medium mb-2">General Settings</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name
                    </label>
                    <input 
                      type="text" 
                      className="w-full p-2 border rounded" 
                      value="ROI Exteriors"
                      readOnly 
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      System Email
                    </label>
                    <input 
                      type="email" 
                      className="w-full p-2 border rounded" 
                      value="system@roiexteriors.com"
                      readOnly
                    />
                  </div>
                </div>
                
                <div className="border rounded p-4">
                  <h3 className="font-medium mb-2">Security Settings</h3>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password Expiry (days)
                    </label>
                    <input 
                      type="number" 
                      className="w-full p-2 border rounded" 
                      value="90"
                      readOnly 
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Two-Factor Authentication
                    </label>
                    <select className="w-full p-2 border rounded">
                      <option>Optional</option>
                      <option>Required for Admins</option>
                      <option>Required for All Users</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded">
                  Save Settings
                </button>
              </div>
              
              <div className="mt-4 text-sm text-gray-500">
                <p>Note: This is a demo. Settings are not saved.</p>
              </div>
            </div>
          )}
          
          {activeTab === 'business-rules' && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Business Rules Configuration</h2>
              <BusinessRulesManager />
            </div>
          )}
          
          {activeTab === 'bulk-operations' && (
            <div>
              <h2 className="text-lg font-semibold mb-4">Bulk Rule Upgrades</h2>
              <RuleUpgradeManager />
            </div>
          )}
          
          {activeTab === 'audit-trail' && (
            <div>
              <AuditTrailViewer />
            </div>
          )}
          
          {activeTab === 'notifications' && (
            <div>
              <NotificationCenter />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;